import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  CheckCircle,
  CreditCard,
  Download,
  Calendar,
  ArrowRight,
  Home,
  AlertTriangle,
  Loader2,
  ArrowLeft,
} from "lucide-react";
import { useSupabaseAuth } from "@/context/SupabaseAuthContext";
import { useToast } from "@/hooks/use-toast";
import { paymentService } from "@/services/paymentService";
import { subscriptionService } from "@/services/subscriptionService";
import { useAccountTier } from "@/hooks/useAccountTier";

const PaymentSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useSupabaseAuth();
  const { toast } = useToast();
  const { refreshAccountTier } = useAccountTier(user?.id);
  const [paymentDetails, setPaymentDetails] = useState<any>(null);
  const [isVerifying, setIsVerifying] = useState(true);
  const [authCheckComplete, setAuthCheckComplete] = useState(false);
  const [verificationFailed, setVerificationFailed] = useState(false);
  const [errorDetails, setErrorDetails] = useState<string>("");

  const sessionId =
    searchParams.get("session_id") ||
    searchParams.get("token") ||
    searchParams.get("orderID") ||
    searchParams.get("_ptxn") ||
    searchParams.get("trxref") ||
    searchParams.get("reference") ||
    searchParams.get("transaction_id");
  const provider = searchParams.get("provider") || "paystack";
  const planId =
    searchParams.get("plan_id") ||
    searchParams.get("planId") ||
    localStorage.getItem("selectedPlanId");

  // Extract additional payment details
  const paymentStatus = searchParams.get("status");
  const paymentAmount = searchParams.get("amount");
  const paymentCurrency = searchParams.get("currency");
  const isManualRedirect = searchParams.get("manual") === "true";

  // Debug: Log all URL parameters for debugging
  useEffect(() => {
    const allParams = Object.fromEntries(searchParams.entries());
    console.log("🔍 PaymentSuccess URL parameters:", allParams);
    console.log("📋 Extracted values:", {
      sessionId,
      provider,
      planId,
      paymentStatus,
      paymentAmount,
      paymentCurrency,
      isManualRedirect,
    });
  }, [
    searchParams,
    sessionId,
    provider,
    planId,
    paymentStatus,
    paymentAmount,
    paymentCurrency,
    isManualRedirect,
  ]);

  // Wait for authentication to load before proceeding
  useEffect(() => {
    const checkAuth = async () => {
      // Wait a bit for auth to initialize
      await new Promise((resolve) => setTimeout(resolve, 1000));

      if (!authLoading) {
        setAuthCheckComplete(true);

        if (!user) {
          console.warn("⚠️ User not authenticated, redirecting to login...");
          toast({
            title: "Authentication Required",
            description: "Please log in to view your payment status.",
            variant: "destructive",
          });
          navigate(
            "/auth?redirect=/payment/success?" + searchParams.toString(),
          );
        }
      }
    };

    checkAuth();
  }, [authLoading, user, navigate, toast, searchParams]);

  useEffect(() => {
    const verifyPayment = async () => {
      // Don't proceed until auth check is complete
      if (!authCheckComplete) {
        return;
      }

      if (!sessionId) {
        console.warn("⚠️ Missing sessionId in URL parameters");
        toast({
          title: "Payment Session Not Found",
          description:
            "Unable to find payment session. Please contact support.",
          variant: "destructive",
        });
        setIsVerifying(false);
        return;
      }

      if (!user?.id) {
        console.warn("⚠️ User ID not available:", { user, authLoading });
        toast({
          title: "User Authentication Error",
          description:
            "Unable to verify user identity. Please try logging in again.",
          variant: "destructive",
        });
        setIsVerifying(false);
        return;
      }

      try {
        console.log("🔍 Starting payment verification...", {
          sessionId,
          provider,
          userId: user.id,
          planId,
        });

        // Show loading state with helpful message
        toast({
          title: "Verifying Payment",
          description:
            "We're confirming your payment with our servers. This may take a moment...",
        });

        // Verify payment and process subscription (now has retry logic)
        const isVerified = await paymentService.verifyPayment(
          sessionId,
          provider as any,
          user.id,
          planId || "PAYG",
        );

        if (isVerified) {
          setPaymentDetails({
            sessionId,
            provider,
            planId: planId || "PAYG",
            amount:
              paymentAmount ||
              searchParams.get("amount") ||
              searchParams.get("value") ||
              getDefaultAmount(planId),
            currency: paymentCurrency || searchParams.get("currency") || "ZAR",
            status: paymentStatus || "completed",
            manual: isManualRedirect,
          });

          toast({
            title: "Payment Verified ✅",
            description: isManualRedirect
              ? planId?.startsWith("credits-")
                ? `Your ${getPlanDisplayName(planId)} purchase was completed successfully!`
                : "Your payment was completed successfully! Welcome to your new plan."
              : planId?.startsWith("credits-")
                ? `Your ${getPlanDisplayName(planId)} have been added to your account.`
                : "Your subscription has been successfully activated.",
          });

          // Refresh user account tier for immediate UI update (only for subscription plans, not credit purchases)
          if (planId && planId !== "PAYG" && !planId.startsWith("credits-")) {
            console.log("👑 Updating account tier to paid...");
            await subscriptionService.updateUserAccountTier(user.id, "paid");
            // Refresh the account tier in the context/state
            refreshAccountTier();
            console.log("✅ Account tier refreshed");
          }
        } else {
          console.error("❌ Payment verification failed");
          setVerificationFailed(true);
          setErrorDetails(
            "Backend verification failed - this may be normal for sandbox testing",
          );

          toast({
            title: "Verification Issue",
            description:
              "Backend verification failed. You can try manual processing below.",
            variant: "destructive",
          });
        }
      } catch (error) {
        console.error("💥 Payment verification error:", error);
        setVerificationFailed(true);
        setErrorDetails(
          error instanceof Error ? error.message : "Network or API error",
        );

        toast({
          title: "Verification Error",
          description:
            "Unable to verify payment. You can try manual processing below.",
          variant: "destructive",
        });
      } finally {
        setIsVerifying(false);
      }
    };

    verifyPayment();
  }, [
    authCheckComplete,
    sessionId,
    provider,
    toast,
    searchParams,
    user?.id,
    planId,
    refreshAccountTier,
  ]);

  const handleManualVerification = async () => {
    if (!user?.id || !sessionId) return;

    const confirmed = window.confirm(
      "Are you sure your Paystack payment was successful? This will manually process your subscription.\n\n" +
        "Only click OK if:\n" +
        "• Paystack showed payment successful\n" +
        "• Money was charged from your account\n" +
        "• You received Paystack confirmation email",
    );

    if (confirmed) {
      try {
        setIsVerifying(true);
        const success = await paymentService.manualPaymentProcessing(
          sessionId,
          provider as any,
          user.id,
          planId || "PAYG",
          true,
        );

        if (success) {
          setVerificationFailed(false);
          setPaymentDetails({
            sessionId,
            provider,
            planId: planId || "PAYG",
            amount: paymentAmount || getDefaultAmount(planId),
            currency: paymentCurrency || "ZAR",
            status: paymentStatus || "completed",
            manual: true,
          });

          // Refresh account tier
          if (planId && planId !== "PAYG") {
            await subscriptionService.updateUserAccountTier(user.id, "paid");
            refreshAccountTier();
          }

          toast({
            title: "Payment Processed",
            description: "Your subscription has been manually activated.",
          });
        } else {
          toast({
            title: "Manual Processing Failed",
            description:
              "Unable to process payment manually. Please contact support.",
            variant: "destructive",
          });
        }
      } catch (error) {
        console.error("Manual verification error:", error);
        toast({
          title: "Processing Error",
          description: "Manual processing failed. Please contact support.",
          variant: "destructive",
        });
      } finally {
        setIsVerifying(false);
      }
    }
  };

  const getProviderDisplayName = (provider: string) => {
    switch (provider) {
      case "payfast":
        return "PayFast";
      case "stripe":
        return "Stripe";
      case "paystack":
        return "PayStack";
      default:
        return provider;
    }
  };

  const getDefaultAmount = (planId: string | null) => {
    switch (planId) {
      case "professional":
        return "1159.92"; // R1159.92 ZAR
      case "credits-5":
      case "credit_5":
        return "80.55"; // R80.55 ZAR
      case "credits-10":
      case "credit_10":
        return "144.89"; // R144.89 ZAR
      case "credits-15":
      case "credit_15":
        return "217.28"; // R217.28 ZAR
      case "credits-20":
      case "credit_20":
        return "289.79"; // R289.79 ZAR
      default:
        return "321.99";
    }
  };

  const getPlanDisplayName = (planId: string | null) => {
    switch (planId) {
      case "PAYG":
        return "PAYG Plan";
      case "professional":
        return "Professional Plan";
      case "credits-5":
        return "5 Credits";
      case "credits-10":
        return "10 Credits";
      case "credits-15":
        return "15 Credits";
      case "credits-20":
        return "20 Credits";
      default:
        if (planId?.startsWith("credits-") || planId?.startsWith("credit_")) {
          const amount = planId.includes("-")
            ? planId.split("-")[1]
            : planId.split("_")[1];
          return `${amount} Credits`;
        }
        return "Subscription Plan";
    }
  };

  if (authLoading || !authCheckComplete) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center space-y-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              <p className="text-center text-muted-foreground">
                Loading user authentication...
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isVerifying) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center space-y-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              <p className="text-center text-muted-foreground">
                Verifying your payment...
              </p>
              <p className="text-center text-sm text-muted-foreground">
                Session: {sessionId?.substring(0, 8)}...
              </p>
              <p className="text-center text-xs text-muted-foreground bg-blue-50 dark:bg-blue-950 p-2 rounded">
                💡 We're waiting for payment confirmation from {provider}.<br />
                This may take up to 30 seconds.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container flex h-16 items-center">
          <Link
            to="/"
            className="flex items-center space-x-2 hover:text-[#40e0d0] transition-colors duration-200"
          >
            <span className="text-xl font-bold">Myǃhū</span>
          </Link>
          {/* Beta and Version Badge */}
          <div className="flex items-center gap-2 ml-4">
            <span className="text-[11px] font-medium text-amber-500 bg-amber-500/10 px-2 py-0.5 rounded-full border border-amber-500/20">
              Beta
            </span>
            <span className="text-[11px] text-muted-foreground">v1.1</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container py-12">
        <div className="max-w-2xl mx-auto">
          {verificationFailed ? (
            <Card className="border-orange-200 dark:border-orange-800 bg-orange-50/50 dark:bg-orange-950/50">
              <CardHeader className="text-center pb-6">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-orange-100 dark:bg-orange-900">
                  <AlertTriangle className="h-8 w-8 text-orange-600 dark:text-orange-400" />
                </div>
                <CardTitle className="text-2xl text-orange-800 dark:text-orange-200">
                  Payment Verification Issue
                </CardTitle>
                <p className="text-muted-foreground">
                  There was an issue verifying your Paystack payment
                  automatically. If your payment was successful in Paystack, you
                  can process it manually.
                </p>
              </CardHeader>

              <CardContent className="space-y-6">
                {/* Error Details */}
                <div className="bg-background rounded-lg p-4 border border-orange-200 dark:border-orange-800 text-center">
                  <h3 className="font-semibold mb-3 flex items-center gap-2 text-orange-700 dark:text-orange-300">
                    <AlertTriangle className="h-4 w-4" />
                    Verification Details
                  </h3>
                  <div className="text-sm space-y-2">
                    <p>
                      <strong>Status:</strong> Backend verification failed
                    </p>
                    <p>
                      <strong>Common Cause:</strong> Paystack API connectivity
                    </p>
                    <p>
                      <strong>Error:</strong> {errorDetails}
                    </p>
                  </div>
                </div>

                {/* Manual Processing */}
                <div className="bg-background rounded-lg p-4 border">
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <CreditCard className="h-4 w-4" />
                    Manual Processing
                  </h3>
                  <div className="space-y-4">
                    <div className="text-sm">
                      <p className="font-medium mb-2">
                        If your Paystack payment was successful:
                      </p>
                      <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
                        <li>Paystack showed "Payment Successful" message</li>
                        <li>Money was charged from your account</li>
                        <li>You received Paystack confirmation email</li>
                      </ul>
                    </div>

                    <Button
                      onClick={handleManualVerification}
                      disabled={isVerifying}
                      className="w-full bg-green-600 hover:bg-green-700"
                    >
                      {isVerifying ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Yes, My Payment Was Successful
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                {/* Navigation Options */}
                <div className="flex flex-col gap-4">
                  <Button
                    variant="outline"
                    onClick={() => navigate("/subscriptions")}
                    className="w-full"
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back to Subscriptions
                  </Button>
                </div>

                {/* Support Info */}
                <div className="text-center text-sm text-muted-foreground border-t pt-4">
                  <p>
                    Still having issues? Contact support at{" "}
                    <a
                      href="mailto:support@myhu.app"
                      className="text-primary hover:underline"
                    >
                      support@myhu.app
                    </a>
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-green-200 dark:border-green-800 bg-green-50/50 dark:bg-green-950/50">
              <CardHeader className="text-center pb-6">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-900">
                  <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
                </div>
                <CardTitle className="text-2xl text-green-800 dark:text-green-200">
                  Payment Successful!
                </CardTitle>
                <p className="text-muted-foreground">
                  {planId?.startsWith("credits-")
                    ? `Thank you for your purchase! Your ${getPlanDisplayName(planId)} have been added to your account.`
                    : "Thank you for subscribing to My!hu. Your payment has been processed successfully."}
                </p>
              </CardHeader>

              <CardContent className="space-y-6">
                {/* Payment Summary */}
                {paymentDetails && (
                  <div className="bg-background rounded-lg p-4 border">
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <CreditCard className="h-4 w-4" />
                      Payment Details
                    </h3>

                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Plan:</span>
                        <span className="font-medium text-foreground">
                          {getPlanDisplayName(planId)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Amount:</span>
                        <span className="font-medium text-foreground">
                          {paymentDetails.currency === "ZAR" ? "R" : "$"}
                          {paymentDetails.currency === "ZAR" &&
                          paymentDetails.amount > 1000
                            ? (parseFloat(paymentDetails.amount) / 100).toFixed(
                                2,
                              )
                            : paymentDetails.amount}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          Payment Method:
                        </span>
                        <span className="font-medium text-foreground">
                          {getProviderDisplayName(provider)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          Transaction ID:
                        </span>
                        <span className="font-mono text-xs bg-muted px-2 py-1 rounded text-foreground">
                          {sessionId?.substring(0, 20)}...
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* What's Next */}
                <div className="bg-background rounded-lg p-4 border">
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    What's Next?
                  </h3>

                  <div className="space-y-3 text-sm">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400 mt-0.5" />
                      <div>
                        <p className="font-medium text-foreground">
                          {planId?.startsWith("credits-")
                            ? "Credits Added"
                            : "Subscription Activated"}
                        </p>
                        <p className="text-muted-foreground">
                          {planId?.startsWith("credits-")
                            ? "Your credits are now available in your account and ready to use."
                            : "Your subscription is now active and ready to use."}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400 mt-0.5" />
                      <div>
                        <p className="font-medium text-foreground">
                          Email Confirmation
                        </p>
                        <p className="text-muted-foreground">
                          You'll receive a confirmation email shortly.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400 mt-0.5" />
                      <div>
                        <p className="font-medium text-foreground">
                          {planId?.startsWith("credits-")
                            ? "Start Using Credits"
                            : "Access Premium Features"}
                        </p>
                        <p className="text-muted-foreground">
                          {planId?.startsWith("credits-")
                            ? "Use your credits to generate reports, predictions, and exports from the dashboard."
                            : "All premium features are now unlocked in your dashboard."}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button
                    onClick={() => navigate("/dashboard")}
                    className="flex-1"
                  >
                    <Home className="h-4 w-4 mr-2" />
                    Go to Dashboard
                  </Button>

                  {/* Only show subscription management for actual subscriptions, not credit purchases */}
                  {planId &&
                    !planId.startsWith("credits-") &&
                    !planId.startsWith("credit_") && (
                      <Button
                        variant="outline"
                        onClick={() => navigate("/subscriptions")}
                        className="flex-1"
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Manage Subscription
                      </Button>
                    )}
                </div>

                {/* Support Info */}
                <div className="text-center text-sm text-muted-foreground border-t pt-4">
                  <p>
                    Need help? Contact our support team at{" "}
                    <a
                      href="mailto:support@myhu.app"
                      className="text-primary hover:underline"
                    >
                      support@myhu.app
                    </a>
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
};

export default PaymentSuccess;
